import { useState } from "react";
import "@/App.css";
import { Toaster, toast } from "sonner";
import {
  ShieldCheck, Award, Building2, Users, Phone, Mail, MapPin,
  CheckCircle2, FileText, Calculator, ArrowRight, Copy, Menu, X,
  BadgeCheck, Clock, RefreshCcw, Landmark, MessageCircle
} from "lucide-react";

const INFO = {
  name: "M/S ARYAN INFOTECH ENTERPRISES",
  proprietor: "VIJAY KUMAR VERMA",
  gstin: "09AXVPV7449N1ZY",
  address: "105, Fortune Heights, First Floor, Indira Nagar, C Block, Lucknow, Uttar Pradesh - 226016",
  phone: "7017546225",
  email: "infotecharyan03@gmail.com",
};

const SERVICES = [
  {
    id: "security",
    testId: "security-guard-outsourcing",
    icon: ShieldCheck,
    title: "Security Guards Outsourcing",
    tagline: "Disciplined, Vigilant & Background-Verified Security Personnel",
    description:
      "Professional security guard outsourcing for corporate offices, commercial complexes, industrial plants, educational institutions, residential societies, and high-profile events across UP & North India.",
    features: ["Trained & uniformed guards", "Background-verified personnel", "Supervision & quality audits", "Rapid replacement support"],
  },
  {
    id: "govt",
    testId: "govt-sector-manpower",
    icon: Landmark,
    title: "Government Sector Manpower Supply",
    tagline: "Compliant, Tender-Ready Staffing for Public Offices & Departments",
    description:
      "Providing clerical staff, data entry operators, multi-tasking staff (MTS), technical support assistants, and office attendants to state and central government departments.",
    features: ["Tender & contract ready", "EPF / ESIC / GST compliant", "Clerical & MTS staffing", "Technical support assistants"],
  },
  {
    id: "private",
    testId: "private-sector-manpower",
    icon: Users,
    title: "Private Sector Manpower Supply",
    tagline: "Tailored Skilled & Semi-Skilled Workforce for Private Enterprises",
    description:
      "End-to-end manpower solutions including facility management, supervisors, warehouse operators, housekeeping staff, and administrative support staff.",
    features: ["Facility management staff", "Housekeeping & support staff", "Warehouse operators", "Custom staffing models"],
  },
];

const NAV_LINKS = [
  { id: "home", label: "Home", testId: "nav-link-home" },
  { id: "about", label: "About Us", testId: "nav-link-about" },
  { id: "services", label: "Services", testId: "nav-link-services" },
  { id: "certifications", label: "Certifications", testId: "nav-link-certifications" },
  { id: "contact", label: "Contact", testId: "nav-link-contact" },
];

function copyToClipboard(text, label) {
  navigator.clipboard.writeText(text).then(() => toast.success(`${label} copied to clipboard`));
}

function TopBar() {
  return (
    <div className="bg-[#1E3A8A] text-white text-xs sm:text-sm">
      <div className="max-w-7xl mx-auto px-4 py-2 flex flex-wrap items-center justify-center sm:justify-between gap-x-6 gap-y-1">
        <div className="flex items-center gap-4 flex-wrap justify-center">
          <span data-testid="top-bar-gstin" className="font-mono tracking-wide">GSTIN: {INFO.gstin}</span>
          <span data-testid="top-bar-iso-badge" className="flex items-center gap-1 text-amber-300 font-semibold">
            <Award size={14} /> ISO 9001:2015 Certified
          </span>
        </div>
        <div className="flex items-center gap-4">
          <a data-testid="top-bar-phone-link" href={`tel:+91${INFO.phone}`} className="flex items-center gap-1 hover:text-amber-300 transition-colors">
            <Phone size={13} /> {INFO.phone}
          </a>
          <a data-testid="top-bar-email-link" href={`mailto:${INFO.email}`} className="hidden md:flex items-center gap-1 hover:text-amber-300 transition-colors">
            <Mail size={13} /> {INFO.email}
          </a>
        </div>
      </div>
    </div>
  );
}

function Navbar() {
  const [open, setOpen] = useState(false);
  const scrollTo = (id) => {
    setOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };
  return (
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
        <button data-testid="navbar-logo-brand" onClick={() => scrollTo("home")} className="flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-lg bg-[#1E3A8A] text-white flex items-center justify-center font-extrabold text-sm font-heading">AI</span>
          <span className="font-heading font-bold text-slate-900 text-sm sm:text-base leading-tight text-left">
            M/S ARYAN INFOTECH<span className="block text-[10px] font-medium tracking-widest text-amber-700">ENTERPRISES</span>
          </span>
        </button>
        <nav className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map((l) => (
            <button key={l.id} data-testid={l.testId} onClick={() => scrollTo(l.id)} className="text-sm font-medium text-slate-600 hover:text-[#1E3A8A] transition-colors">
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-btn-request-quote"
            onClick={() => scrollTo("contact")}
            className="bg-[#1E3A8A] hover:bg-blue-800 text-white text-sm font-semibold rounded-lg px-5 py-2.5 shadow-sm hover:shadow active:scale-95 transition-all"
          >
            Request Quote
          </button>
        </nav>
        <button data-testid="nav-mobile-menu-btn" className="lg:hidden text-slate-700" onClick={() => setOpen(!open)} aria-label="Toggle menu">
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white px-4 py-3 flex flex-col gap-1">
          {NAV_LINKS.map((l) => (
            <button key={l.id} data-testid={`mobile-${l.testId}`} onClick={() => scrollTo(l.id)} className="text-left py-2.5 text-sm font-medium text-slate-700 hover:text-[#1E3A8A] border-b border-slate-100 last:border-0">
              {l.label}
            </button>
          ))}
          <button data-testid="mobile-nav-btn-request-quote" onClick={() => scrollTo("contact")} className="mt-2 bg-[#1E3A8A] text-white text-sm font-semibold rounded-lg px-5 py-2.5">
            Request Quote
          </button>
        </div>
      )}
    </header>
  );
}

function Hero() {
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  return (
    <section id="home" className="relative bg-[#F8FAFC] overflow-hidden">
      <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 15% 20%, rgba(30,58,138,0.06), transparent 40%), radial-gradient(circle at 85% 70%, rgba(217,119,6,0.06), transparent 40%)" }} />
      <div className="relative max-w-7xl mx-auto px-4 pt-16 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <div className="flex flex-wrap gap-2 mb-6">
            <span data-testid="hero-iso-badge" className="text-xs font-semibold uppercase tracking-wider text-amber-700 bg-amber-50 px-2.5 py-1 rounded-full border border-amber-200/60 flex items-center gap-1">
              <Award size={13} /> ISO 9001:2015 Certified
            </span>
            <span data-testid="hero-msme-badge" className="text-xs font-semibold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-200/60 flex items-center gap-1">
              <BadgeCheck size={13} /> MSME Registered
            </span>
          </div>
          <h1 data-testid="hero-main-heading" className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-tight">
            Trusted Security Guards & <span className="text-[#1E3A8A]">Manpower Solutions</span>
          </h1>
          <p data-testid="hero-subheading" className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl">
            M/S Aryan Infotech Enterprises supplies disciplined security guards and all types of trained manpower to government departments and private sector organisations — fully compliant, background-verified, and dependable.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <button
              data-testid="hero-cta-quote-modal-btn"
              onClick={() => scrollTo("estimator")}
              className="bg-[#1E3A8A] hover:bg-blue-800 text-white font-semibold rounded-lg px-6 py-3 shadow-sm hover:shadow active:scale-95 transition-all flex items-center gap-2"
            >
              Get Instant Estimate <ArrowRight size={16} />
            </button>
            <a
              data-testid="hero-cta-call-btn"
              href={`tel:+91${INFO.phone}`}
              className="border border-slate-300 bg-white hover:border-[#1E3A8A] hover:text-[#1E3A8A] text-slate-800 font-semibold rounded-lg px-6 py-3 transition-all flex items-center gap-2"
            >
              <Phone size={16} /> Call {INFO.phone}
            </a>
          </div>
          <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
            <div data-testid="hero-stat-manpower-count" className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
              <p className="font-heading text-2xl font-extrabold text-[#1E3A8A]">500+</p>
              <p className="text-xs text-slate-500 mt-0.5">Manpower Deployed</p>
            </div>
            <div data-testid="hero-stat-compliance-rate" className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
              <p className="font-heading text-2xl font-extrabold text-[#1E3A8A]">100%</p>
              <p className="text-xs text-slate-500 mt-0.5">Statutory Compliant</p>
            </div>
            <div data-testid="hero-stat-cert-status" className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
              <p className="font-heading text-2xl font-extrabold text-[#1E3A8A]">ISO</p>
              <p className="text-xs text-slate-500 mt-0.5">9001:2015 Certified</p>
            </div>
          </div>
        </div>
        <div className="relative">
          <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-lg bg-white">
            <img
              src="https://images.unsplash.com/photo-1775531501706-ad733d12d0e7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
              alt="Professional security guard at corporate premises"
              className="w-full h-[340px] sm:h-[420px] object-cover"
              loading="lazy"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          </div>
          <div className="absolute -bottom-5 -left-3 sm:-left-5 bg-white rounded-xl border border-slate-200 shadow-md px-4 py-3 flex items-center gap-3">
            <span className="w-10 h-10 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center"><Award size={20} /></span>
            <div>
              <p className="text-sm font-bold text-slate-900">ISO 9001:2015</p>
              <p className="text-xs text-slate-500">& MSME Certified Organisation</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Certifications() {
  const cards = [
    {
      testId: "cert-iso-card",
      icon: Award,
      iconClass: "bg-amber-50 text-amber-600",
      title: "ISO 9001:2015",
      subtitle: "Quality Management System",
      text: "An ISO 9001:2015 certified organisation, committed to consistent service quality, defined processes, and continual improvement.",
    },
    {
      testId: "cert-msme-card",
      icon: BadgeCheck,
      iconClass: "bg-emerald-50 text-emerald-600",
      title: "MSME Certified",
      subtitle: "Govt. of India Registered",
      text: "Registered under the MSME (Udyam) framework of the Government of India — a recognised and trusted enterprise.",
    },
    {
      testId: "cert-gstin-card",
      icon: FileText,
      iconClass: "bg-sky-50 text-sky-600",
      title: "GST Registered",
      subtitle: "Verified Legal Entity",
      text: null,
    },
  ];
  return (
    <section id="certifications" className="bg-white border-y border-slate-200">
      <div className="max-w-7xl mx-auto px-4 py-16 lg:py-20">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-xs font-semibold uppercase tracking-wider text-amber-700 bg-amber-50 px-2.5 py-1 rounded-full border border-amber-200/60">Trust & Compliance</span>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 mt-4">A Certified & Registered Organisation</h2>
          <p className="text-base text-slate-600 mt-3">Every engagement is backed by statutory registrations and quality certifications you can verify.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {cards.map((c) => (
            <div key={c.testId} data-testid={c.testId} className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm hover:shadow-md transition-shadow duration-300">
              <span className={`w-12 h-12 rounded-xl ${c.iconClass} flex items-center justify-center`}><c.icon size={24} /></span>
              <h3 className="font-heading text-lg sm:text-xl font-semibold text-slate-800 mt-5">{c.title}</h3>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mt-1">{c.subtitle}</p>
              {c.text && <p className="text-sm text-slate-600 leading-relaxed mt-3">{c.text}</p>}
              {c.testId === "cert-gstin-card" && (
                <div className="mt-3 flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5">
                  <span data-testid="cert-gstin-value" className="font-mono text-sm font-semibold text-slate-800 flex-1">{INFO.gstin}</span>
                  <button data-testid="cert-gstin-copy-btn" onClick={() => copyToClipboard(INFO.gstin, "GSTIN")} className="text-slate-400 hover:text-[#1E3A8A] transition-colors" aria-label="Copy GSTIN">
                    <Copy size={15} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function About() {
  const points = [
    "Ethical labour practices & fair wages",
    "EPF, ESIC & GST statutory compliance",
    "Background-verified, trained workforce",
    "Serving government & private sectors",
  ];
  return (
    <section id="about" className="bg-[#F8FAFC]">
      <div className="max-w-7xl mx-auto px-4 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <span className="text-xs font-semibold uppercase tracking-wider text-amber-700 bg-amber-50 px-2.5 py-1 rounded-full border border-amber-200/60">About Us</span>
          <h2 data-testid="about-heading" className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 mt-4">
            Dependable Manpower Partner from the Heart of Lucknow
          </h2>
          <p className="text-base text-slate-600 leading-relaxed mt-5">
            Led by proprietor <span data-testid="about-proprietor-name" className="font-semibold text-slate-800">Vijay Kumar Verma</span>, M/S Aryan Infotech Enterprises is an ISO 9001:2015 and MSME certified organisation specialising in security guard outsourcing and end-to-end manpower supply for government and private sector clients.
          </p>
          <p data-testid="about-address-text" className="text-sm text-slate-500 leading-relaxed mt-4 flex items-start gap-2">
            <MapPin size={16} className="mt-0.5 shrink-0 text-[#1E3A8A]" />
            {INFO.address}
          </p>
          <ul className="mt-6 space-y-3">
            {points.map((p) => (
              <li key={p} className="flex items-center gap-2.5 text-sm font-medium text-slate-700">
                <CheckCircle2 size={17} className="text-emerald-600 shrink-0" /> {p}
              </li>
            ))}
          </ul>
          <button
            data-testid="about-read-more-btn"
            onClick={() => document.getElementById("services")?.scrollIntoView({ behavior: "smooth" })}
            className="mt-8 text-[#1E3A8A] font-semibold text-sm flex items-center gap-1.5 hover:gap-2.5 transition-all"
          >
            Explore our services <ArrowRight size={15} />
          </button>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2 rounded-2xl overflow-hidden border border-slate-200 shadow-sm bg-white">
            <img
              src="https://images.unsplash.com/photo-1754531976828-69e42ce4e0d9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
              alt="Professional workforce team"
              className="w-full h-52 object-cover"
              loading="lazy"
              onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
            />
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
            <span className="w-10 h-10 rounded-lg bg-blue-50 text-[#1E3A8A] flex items-center justify-center"><Building2 size={20} /></span>
            <p className="font-heading text-xl font-bold text-slate-900 mt-3">Lucknow HQ</p>
            <p className="text-xs text-slate-500 mt-1">Indira Nagar, Uttar Pradesh</p>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
            <span className="w-10 h-10 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center"><Users size={20} /></span>
            <p className="font-heading text-xl font-bold text-slate-900 mt-3">All Sectors</p>
            <p className="text-xs text-slate-500 mt-1">Government & Private</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Services() {
  const [active, setActive] = useState("security");
  const current = SERVICES.find((s) => s.id === active);
  const scrollToContact = () => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
  return (
    <section id="services" className="bg-white border-y border-slate-200">
      <div className="max-w-7xl mx-auto px-4 py-16 lg:py-24">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <span className="text-xs font-semibold uppercase tracking-wider text-amber-700 bg-amber-50 px-2.5 py-1 rounded-full border border-amber-200/60">Our Services</span>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 mt-4">What We Provide</h2>
          <p className="text-base text-slate-600 mt-3">Security guards outsourcing and all types of manpower for government and private sectors.</p>
        </div>
        <div className="flex flex-wrap justify-center gap-2 mb-10" role="tablist">
          {SERVICES.map((s) => (
            <button
              key={s.id}
              data-testid={`services-tab-${s.id === "security" ? "security-guards" : s.id === "govt" ? "govt-manpower" : "private-manpower"}`}
              role="tab"
              aria-selected={active === s.id}
              onClick={() => setActive(s.id)}
              className={`px-5 py-2.5 rounded-full text-sm font-semibold border transition-all ${
                active === s.id
                  ? "bg-[#1E3A8A] text-white border-[#1E3A8A] shadow-sm"
                  : "bg-white text-slate-600 border-slate-200 hover:border-[#1E3A8A] hover:text-[#1E3A8A]"
              }`}
            >
              {s.title}
            </button>
          ))}
        </div>
        <div
          key={current.id}
          data-testid={`service-card-${current.testId}`}
          className="max-w-3xl mx-auto bg-[#F8FAFC] rounded-2xl border border-slate-200 p-8 sm:p-10 shadow-sm"
        >
          <div className="flex flex-col sm:flex-row sm:items-start gap-5">
            <span className="w-14 h-14 rounded-xl bg-[#1E3A8A] text-white flex items-center justify-center shrink-0"><current.icon size={28} /></span>
            <div>
              <h3 className="font-heading text-xl sm:text-2xl font-semibold text-slate-800">{current.title}</h3>
              <p className="text-sm font-medium text-amber-700 mt-1">{current.tagline}</p>
              <p className="text-base text-slate-600 leading-relaxed mt-4">{current.description}</p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3 mt-7">
            {current.features.map((f) => (
              <div key={f} className="flex items-center gap-2.5 bg-white border border-slate-200 rounded-lg px-4 py-3 text-sm font-medium text-slate-700">
                <CheckCircle2 size={16} className="text-emerald-600 shrink-0" /> {f}
              </div>
            ))}
          </div>
          <button
            data-testid={`service-inquire-btn-${current.id === "security" ? "security" : current.id}`}
            onClick={scrollToContact}
            className="mt-8 bg-[#1E3A8A] hover:bg-blue-800 text-white font-semibold rounded-lg px-6 py-3 shadow-sm hover:shadow active:scale-95 transition-all flex items-center gap-2"
          >
            Inquire Now <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </section>
  );
}

function WhyChooseUs() {
  const features = [
    { testId: "why-feature-compliance", icon: FileText, title: "100% Legal Compliance", text: "Full adherence to EPF, ESIC, GST and labour laws — audit-ready documentation for every deployment.", className: "md:col-span-2" },
    { testId: "why-feature-verification", icon: ShieldCheck, title: "Background-Checked Staff", text: "Every guard and staff member is police-verified and background screened before deployment." },
    { testId: "why-feature-support", icon: Clock, title: "24/7 Operational Support", text: "Round-the-clock supervision and a dedicated point of contact for your site." },
    { testId: "why-feature-replacement", icon: RefreshCcw, title: "Swift Replacement Guarantee", text: "Any unsuitable or absent staff member is replaced quickly at no extra hassle to you." },
    { testId: "why-feature-custom-models", icon: Building2, title: "Custom Staffing Models", text: "Flexible contracts — short-term, long-term, or project-based — tailored to your sector and budget.", className: "md:col-span-2" },
  ];
  return (
    <section className="bg-[#F8FAFC]">
      <div className="max-w-7xl mx-auto px-4 py-16 lg:py-24">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-xs font-semibold uppercase tracking-wider text-amber-700 bg-amber-50 px-2.5 py-1 rounded-full border border-amber-200/60">Why Choose Us</span>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 mt-4">Built on Trust, Backed by Process</h2>
        </div>
        <div data-testid="why-choose-grid" className="grid md:grid-cols-3 gap-5">
          {features.map((f) => (
            <div key={f.testId} data-testid={f.testId} className={`bg-white rounded-2xl border border-slate-200 p-7 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 ${f.className || ""}`}>
              <span className="w-11 h-11 rounded-xl bg-blue-50 text-[#1E3A8A] flex items-center justify-center"><f.icon size={22} /></span>
              <h3 className="font-heading text-lg font-semibold text-slate-800 mt-4">{f.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed mt-2">{f.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const RATES = { security: 18000, govt: 16000, private: 15000 };
const SHIFT_FACTOR = { "8": 1, "12": 1.4, "24": 1.9 };

function Estimator() {
  const [service, setService] = useState("security");
  const [count, setCount] = useState(10);
  const [shift, setShift] = useState("12");
  const [sector, setSector] = useState("private");
  const [result, setResult] = useState(null);

  const estimate = () => {
    const monthly = Math.round(RATES[service] * count * SHIFT_FACTOR[shift]);
    setResult({ monthly });
    toast.success("Estimate generated — submit the contact form below to get a formal quote.");
  };

  const inputCls = "w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/30 focus:border-[#1E3A8A] transition-all";

  return (
    <section id="estimator" className="bg-white border-y border-slate-200">
      <div className="max-w-7xl mx-auto px-4 py-16 lg:py-24">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-xs font-semibold uppercase tracking-wider text-amber-700 bg-amber-50 px-2.5 py-1 rounded-full border border-amber-200/60">Quick Estimate</span>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 mt-4">Manpower Requirement Estimator</h2>
          <p className="text-base text-slate-600 mt-3">Get a ballpark monthly estimate in seconds, then send us your requirement.</p>
        </div>
        <div data-testid="estimator-container" className="max-w-3xl mx-auto bg-[#F8FAFC] rounded-2xl border border-slate-200 p-8 sm:p-10 shadow-sm">
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label htmlFor="est-service" className="text-xs font-semibold uppercase tracking-wider text-slate-500">Service Type</label>
              <select id="est-service" data-testid="estimator-select-service" value={service} onChange={(e) => setService(e.target.value)} className={`${inputCls} mt-1.5`}>
                <option value="security">Security Guards</option>
                <option value="govt">Government Manpower</option>
                <option value="private">Private Sector Manpower</option>
              </select>
            </div>
            <div>
              <label htmlFor="est-count" className="text-xs font-semibold uppercase tracking-wider text-slate-500">Staff Required</label>
              <input id="est-count" data-testid="estimator-input-staff-count" type="number" min="1" max="1000" value={count} onChange={(e) => setCount(Math.max(1, Number(e.target.value) || 1))} className={`${inputCls} mt-1.5`} />
            </div>
            <div>
              <label htmlFor="est-shift" className="text-xs font-semibold uppercase tracking-wider text-slate-500">Shift Duration</label>
              <select id="est-shift" data-testid="estimator-select-shift" value={shift} onChange={(e) => setShift(e.target.value)} className={`${inputCls} mt-1.5`}>
                <option value="8">8 Hours</option>
                <option value="12">12 Hours</option>
                <option value="24">24 Hours</option>
              </select>
            </div>
            <div>
              <label htmlFor="est-sector" className="text-xs font-semibold uppercase tracking-wider text-slate-500">Sector</label>
              <select id="est-sector" data-testid="estimator-select-sector" value={sector} onChange={(e) => setSector(e.target.value)} className={`${inputCls} mt-1.5`}>
                <option value="private">Private Sector</option>
                <option value="govt">Government Sector</option>
              </select>
            </div>
          </div>
          <button
            data-testid="estimator-submit-btn"
            onClick={estimate}
            className="mt-7 w-full sm:w-auto bg-[#1E3A8A] hover:bg-blue-800 text-white font-semibold rounded-lg px-8 py-3 shadow-sm hover:shadow active:scale-95 transition-all flex items-center justify-center gap-2"
          >
            <Calculator size={17} /> Calculate Estimate
          </button>
          {result && (
            <div data-testid="estimator-result-display" className="mt-6 bg-white border border-emerald-200 rounded-xl p-6 flex flex-col sm:flex-row sm:items-center gap-4">
              <span className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0"><Calculator size={22} /></span>
              <div className="flex-1">
                <p className="font-heading text-2xl font-extrabold text-slate-900">≈ ₹{result.monthly.toLocaleString("en-IN")}<span className="text-sm font-medium text-slate-500"> / month</span></p>
                <p className="text-xs text-slate-500 mt-1">Indicative estimate for {count} staff · {shift}h shift · {sector === "govt" ? "Government" : "Private"} sector. Final quote on inquiry.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "Security Guards Outsourcing", message: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, phone number, and message.");
      return;
    }
    const subject = encodeURIComponent(`Inquiry: ${form.service} — ${form.name}`);
    const body = encodeURIComponent(`Name: ${form.name}\nPhone: ${form.phone}\nEmail: ${form.email}\nService: ${form.service}\n\n${form.message}`);
    window.location.href = `mailto:${INFO.email}?subject=${subject}&body=${body}`;
    toast.success("Opening your email app with the inquiry details.");
    setForm({ name: "", email: "", phone: "", service: "Security Guards Outsourcing", message: "" });
  };

  const inputCls = "w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/30 focus:border-[#1E3A8A] transition-all";

  return (
    <section id="contact" className="bg-[#F8FAFC]">
      <div className="max-w-7xl mx-auto px-4 py-16 lg:py-24">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-xs font-semibold uppercase tracking-wider text-amber-700 bg-amber-50 px-2.5 py-1 rounded-full border border-amber-200/60">Get In Touch</span>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 mt-4">Request a Quote or Callback</h2>
        </div>
        <div className="grid lg:grid-cols-5 gap-8 max-w-5xl mx-auto">
          <div className="lg:col-span-2 space-y-4">
            <div data-testid="contact-info-address" className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm flex items-start gap-3">
              <span className="w-10 h-10 rounded-lg bg-blue-50 text-[#1E3A8A] flex items-center justify-center shrink-0"><MapPin size={19} /></span>
              <p className="text-sm text-slate-600 leading-relaxed">{INFO.address}</p>
            </div>
            <a data-testid="contact-info-phone" href={`tel:+91${INFO.phone}`} className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm flex items-center gap-3 hover:border-[#1E3A8A] transition-colors">
              <span className="w-10 h-10 rounded-lg bg-blue-50 text-[#1E3A8A] flex items-center justify-center shrink-0"><Phone size={19} /></span>
              <div><p className="text-xs text-slate-500">Call us</p><p className="text-sm font-semibold text-slate-800">+91 {INFO.phone}</p></div>
            </a>
            <a data-testid="contact-info-email" href={`mailto:${INFO.email}`} className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm flex items-center gap-3 hover:border-[#1E3A8A] transition-colors">
              <span className="w-10 h-10 rounded-lg bg-blue-50 text-[#1E3A8A] flex items-center justify-center shrink-0"><Mail size={19} /></span>
              <div><p className="text-xs text-slate-500">Email us</p><p className="text-sm font-semibold text-slate-800 break-all">{INFO.email}</p></div>
            </a>
            <div data-testid="contact-info-gstin" className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm flex items-center gap-3">
              <span className="w-10 h-10 rounded-lg bg-blue-50 text-[#1E3A8A] flex items-center justify-center shrink-0"><FileText size={19} /></span>
              <div className="flex-1"><p className="text-xs text-slate-500">GSTIN</p><p className="font-mono text-sm font-semibold text-slate-800">{INFO.gstin}</p></div>
              <button data-testid="contact-gstin-copy-btn" onClick={() => copyToClipboard(INFO.gstin, "GSTIN")} className="text-slate-400 hover:text-[#1E3A8A] transition-colors" aria-label="Copy GSTIN"><Copy size={15} /></button>
            </div>
            <a
              data-testid="contact-whatsapp-direct-btn"
              href={`https://wa.me/91${INFO.phone}?text=${encodeURIComponent("Hello, I want to inquire about your security & manpower services.")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl p-5 shadow-sm flex items-center gap-3 transition-colors"
            >
              <MessageCircle size={20} />
              <div><p className="text-sm font-bold">Chat on WhatsApp</p><p className="text-xs text-emerald-100">Fastest response</p></div>
            </a>
          </div>
          <form onSubmit={submit} className="lg:col-span-3 bg-white rounded-2xl border border-slate-200 p-7 sm:p-8 shadow-sm space-y-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="cf-name" className="text-xs font-semibold uppercase tracking-wider text-slate-500">Name *</label>
                <input id="cf-name" data-testid="contact-form-name-input" value={form.name} onChange={set("name")} placeholder="Your full name" className={`${inputCls} mt-1.5`} />
              </div>
              <div>
                <label htmlFor="cf-phone" className="text-xs font-semibold uppercase tracking-wider text-slate-500">Phone *</label>
                <input id="cf-phone" data-testid="contact-form-phone-input" value={form.phone} onChange={set("phone")} placeholder="Your mobile number" className={`${inputCls} mt-1.5`} />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="cf-email" className="text-xs font-semibold uppercase tracking-wider text-slate-500">Email</label>
                <input id="cf-email" data-testid="contact-form-email-input" type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className={`${inputCls} mt-1.5`} />
              </div>
              <div>
                <label htmlFor="cf-service" className="text-xs font-semibold uppercase tracking-wider text-slate-500">Service Required</label>
                <select id="cf-service" data-testid="contact-form-service-select" value={form.service} onChange={set("service")} className={`${inputCls} mt-1.5`}>
                  <option>Security Guards Outsourcing</option>
                  <option>Government Sector Manpower</option>
                  <option>Private Sector Manpower</option>
                  <option>Other Requirement</option>
                </select>
              </div>
            </div>
            <div>
              <label htmlFor="cf-message" className="text-xs font-semibold uppercase tracking-wider text-slate-500">Message *</label>
              <textarea id="cf-message" data-testid="contact-form-message-textarea" rows={4} value={form.message} onChange={set("message")} placeholder="Tell us your requirement — staff count, location, shift timings…" className={`${inputCls} mt-1.5 resize-none`} />
            </div>
            <button
              data-testid="contact-form-submit-button"
              type="submit"
              className="w-full sm:w-auto bg-[#1E3A8A] hover:bg-blue-800 text-white font-semibold rounded-lg px-8 py-3 shadow-sm hover:shadow active:scale-95 transition-all flex items-center justify-center gap-2"
            >
              Send Inquiry <ArrowRight size={16} />
            </button>
          </form>
        </div>
        <div data-testid="office-map-container" className="max-w-5xl mx-auto mt-10">
          <div className="bg-white rounded-2xl border border-slate-200 p-2 shadow-sm overflow-hidden">
            <iframe
              data-testid="office-map-iframe"
              title="M/S Aryan Infotech Enterprises office location — Fortune Heights, Indira Nagar, Lucknow"
              src="https://www.google.com/maps?q=Fortune+Heights,+Indira+Nagar,+C+Block,+Lucknow,+Uttar+Pradesh+226016&output=embed"
              className="w-full h-72 sm:h-80 rounded-xl border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
          <a
            data-testid="office-map-directions-btn"
            href="https://www.google.com/maps/dir/?api=1&destination=Fortune+Heights,+Indira+Nagar,+C+Block,+Lucknow,+Uttar+Pradesh+226016"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#1E3A8A] hover:gap-3 transition-all"
          >
            <MapPin size={15} /> Get Directions to our office
          </a>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  return (
    <footer className="bg-[#0F172A] text-slate-300">
      <div className="max-w-7xl mx-auto px-4 py-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-10">
        <div>
          <p data-testid="footer-company-name" className="font-heading text-lg font-bold text-white">{INFO.name}</p>
          <p className="text-xs text-slate-400 mt-1">Proprietor: {INFO.proprietor}</p>
          <p className="text-xs text-amber-400 mt-2 flex items-center gap-1.5"><Award size={13} /> ISO 9001:2015 & MSME Certified Organisation</p>
          <p data-testid="footer-gstin" className="font-mono text-xs text-slate-400 mt-3">GSTIN: {INFO.gstin}</p>
        </div>
        <div>
          <p className="text-sm font-semibold text-white uppercase tracking-wider mb-4">Quick Links</p>
          <div className="flex flex-col gap-2.5">
            {NAV_LINKS.map((l) => (
              <button key={l.id} data-testid={`footer-link-${l.id}`} onClick={() => scrollTo(l.id)} className="text-left text-sm text-slate-400 hover:text-white transition-colors w-fit">
                {l.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <p className="text-sm font-semibold text-white uppercase tracking-wider mb-4">Contact</p>
          <p data-testid="footer-address" className="text-sm text-slate-400 leading-relaxed">{INFO.address}</p>
          <a data-testid="footer-phone" href={`tel:+91${INFO.phone}`} className="block text-sm text-slate-400 hover:text-white mt-3 transition-colors">+91 {INFO.phone}</a>
          <a data-testid="footer-email" href={`mailto:${INFO.email}`} className="block text-sm text-slate-400 hover:text-white mt-1.5 transition-colors break-all">{INFO.email}</a>
        </div>
      </div>
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-5 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p data-testid="footer-copyright" className="text-xs text-slate-500">© {new Date().getFullYear()} {INFO.name}. All rights reserved.</p>
          <p className="text-xs text-slate-600">Lucknow, Uttar Pradesh, India</p>
        </div>
      </div>
    </footer>
  );
}

export default function App() {
  return (
    <div className="App min-h-screen bg-[#F8FAFC]">
      <Toaster position="top-center" richColors />
      <TopBar />
      <Navbar />
      <Hero />
      <Certifications />
      <About />
      <Services />
      <WhyChooseUs />
      <Estimator />
      <Contact />
      <Footer />
    </div>
  );
}
